import { API_BASE_URL } from '../config';
import React, { useState, useEffect } from 'react';

function courseSummary(procedure) {
  if (procedure.SERVICE_KIND === 'Package') return `Package, ${procedure.SESSION_COUNT || 1} sittings`;
  if (procedure.SERVICE_KIND === 'Course') return `${procedure.SESSION_COUNT || 1} sittings, every ${procedure.SESSION_GAP_DAYS || 1} day(s)`;
  return 'Single visit';
}

function ProcedureCourseFields({ data, procedures, selfId, onChange }) {
  const kind = data.SERVICE_KIND || 'Single';
  const components = Array.isArray(data.COMPONENTS) ? data.COMPONENTS : [];
  const choices = procedures.filter((item) => item._id !== selfId && item.SERVICE_KIND !== 'Package');
  const componentId = (item) => item.PROCEDURE_ID?._id || item.PROCEDURE_ID || '';
  return (
    <>
      <div style={{ marginBottom: '1rem' }}>
        <label>How it is given:</label><br />
        <select value={kind} onChange={(event) => onChange({
          SERVICE_KIND: event.target.value,
          BILLING_MODE: event.target.value === 'Single' ? 'PerSession' : 'Once',
          SESSION_COUNT: event.target.value === 'Single' ? 1 : (Number(data.SESSION_COUNT) > 1 ? data.SESSION_COUNT : 7)
        })} style={{ width: '100%', padding: '0.5rem' }}>
          <option value="Single">Single visit</option>
          <option value="Course">Course of sittings</option>
          <option value="Package">Package of therapies</option>
        </select>
      </div>
      {kind === 'Course' && (
        <div style={{ marginBottom: '1rem' }}>
          <label>Sittings:</label><br />
          <input type="number" min="2" max="60" value={data.SESSION_COUNT || 2} onChange={(event) => onChange({ SESSION_COUNT: Number(event.target.value) })} style={{ width: '100%' }} />
        </div>
      )}
      {kind !== 'Single' && (
        <div style={{ marginBottom: '1rem' }}>
          <label>Days between sittings:</label><br />
          <input type="number" min="1" max="30" value={data.SESSION_GAP_DAYS || 1} onChange={(event) => onChange({ SESSION_GAP_DAYS: Number(event.target.value) })} style={{ width: '100%' }} />
        </div>
      )}
      <div style={{ marginBottom: '1rem' }}>
        <label>Billing:</label><br />
        <select value={data.BILLING_MODE || (kind === 'Single' ? 'PerSession' : 'Once')} onChange={(event) => onChange({ BILLING_MODE: event.target.value })} style={{ width: '100%', padding: '0.5rem' }}>
          <option value="Once">Charge the full amount once</option>
          <option value="PerSession">Charge each sitting</option>
        </select>
      </div>
      {kind === 'Package' && (
        <div style={{ marginBottom: '1rem' }}>
          <label>Therapies in this package:</label>
          {components.map((item, index) => (
            <div key={`${componentId(item)}-${index}`} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px auto', gap: '0.35rem', marginTop: '0.35rem' }}>
              <select value={componentId(item)} onChange={(event) => {
                const next = components.slice();
                next[index] = { ...item, PROCEDURE_ID: event.target.value };
                onChange({ COMPONENTS: next });
              }} style={{ width: '100%', padding: '0.4rem' }}>
                <option value="">Select therapy</option>
                {choices.map((procedure) => <option key={procedure._id} value={procedure._id}>{procedure.PROCEDURE_NAME}</option>)}
              </select>
              <input type="number" min="1" max="60" title="Sittings" value={item.SESSION_COUNT || 1} onChange={(event) => {
                const next = components.slice();
                next[index] = { ...item, SESSION_COUNT: Number(event.target.value) };
                onChange({ COMPONENTS: next });
              }} />
              <input type="number" min="1" max="30" title="Days between sittings" value={item.SESSION_GAP_DAYS || 1} onChange={(event) => {
                const next = components.slice();
                next[index] = { ...item, SESSION_GAP_DAYS: Number(event.target.value) };
                onChange({ COMPONENTS: next });
              }} />
              <button type="button" onClick={() => onChange({ COMPONENTS: components.filter((_, itemIndex) => itemIndex !== index) })}>Remove</button>
            </div>
          ))}
          <button type="button" style={{ marginTop: '0.5rem' }} onClick={() => onChange({ COMPONENTS: [...components, { PROCEDURE_ID: '', SESSION_COUNT: 1, SESSION_GAP_DAYS: data.SESSION_GAP_DAYS || 1 }] })}>Add therapy</button>
        </div>
      )}
    </>
  );
}

const ReferenceDataManagement = () => {
  const [selectedTab, setSelectedTab] = useState('doctors');
  const [doctorsList, setDoctorsList] = useState([]);
  const [doctorsLoading, setDoctorsLoading] = useState(true);
  const [doctorsError, setDoctorsError] = useState('');
  const [showAddDoctorModal, setShowAddDoctorModal] = useState(false);
  const [newDoctorFormData, setNewDoctorFormData] = useState({ 
    DOCTOR_NAME: '',
    SPECIALIZATION: '',
    QUALIFICATIONS: '',
    EXPERIENCE_YEARS: 0,
    CONTACT_NUMBER: '',
    EMAIL: '',
    CONSULTATION_HOURS: [],
    CLINIC_LOCATION: '',
    APPOINTMENT_FEES: 0,
    STATUS: 'Active',
    PHOTO_ID: '',
    DEPARTMENT: '',
    BIOGRAPHY: '',
    LANGUAGES_SPOKEN: [],
    AVAILABLE_DAYS: []
  });
  const [addDoctorError, setAddDoctorError] = useState('');
  const [addDoctorLoading, setAddDoctorLoading] = useState(false);
  const [showEditDoctorModal, setShowEditDoctorModal] = useState(false);
  const [editDoctorFormData, setEditDoctorFormData] = useState(null);
  const [editDoctorError, setEditDoctorError] = useState('');
  const [editDoctorLoading, setEditDoctorLoading] = useState(false);

  // Medicine States
  const [medicines, setMedicines] = useState([]);
  const [medicineLoading, setMedicineLoading] = useState(true);
  const [medicineError, setMedicineError] = useState('');
  const [showAddMedicineModal, setShowAddMedicineModal] = useState(false);
  const [newMedicineData, setNewMedicineData] = useState({
    MEDICINE_NAME: '',
    GENERIC_NAME: '',
    BRAND_NAME: '',
    MEDICINE_TYPE: 'Tablet',
    DOSAGE_FORM: '',
    STRENGTH: '',
    QUANTITY_IN_STOCK: 0,
    EXPIRATION_DATE: '',
    SUPPLIER_ID: '',
    SUPPLIER_NAME: '',
    PURCHASE_DATE: '',
    PURCHASE_PRICE: 0,
    SELLING_PRICE: 0,
    BATCH_NUMBER: '',
    STORAGE_LOCATION: '',
    REORDER_LEVEL: 0,
    REORDER_QUANTITY: 1,
    STATUS: 'Available',
    REMARKS: '',
  });
  const [addMedicineError, setAddMedicineError] = useState('');
  const [addMedicineLoading, setAddMedicineLoading] = useState(false);
  const [showEditMedicineModal, setShowEditMedicineModal] = useState(false);
  const [editMedicineData, setEditMedicineData] = useState(null);
  const [editMedicineError, setEditMedicineError] = useState('');
  const [editMedicineLoading, setEditMedicineLoading] = useState(false);

  // Procedure Room States
  const [procedureRooms, setProcedureRooms] = useState([]);
  const [procedureRoomLoading, setProcedureRoomLoading] = useState(true);
  const [procedureRoomError, setProcedureRoomError] = useState('');
  const [showAddProcedureRoomModal, setShowAddProcedureRoomModal] = useState(false);
  const [newProcedureRoomData, setNewProcedureRoomData] = useState({
    ROOM_NAME: '',
    LOCATION: '',
    CAPACITY: 1,
    EQUIPMENT_AVAILABLE: '',
    STATUS: 'Available',
  });
  const [addProcedureRoomError, setAddProcedureRoomError] = useState('');
  const [addProcedureRoomLoading, setAddProcedureRoomLoading] = useState(false);
  const [showEditProcedureRoomModal, setShowEditProcedureRoomModal] = useState(false);
  const [editProcedureRoomData, setEditProcedureRoomData] = useState(null);
  const [editProcedureRoomError, setEditProcedureRoomError] = useState('');
  const [editProcedureRoomLoading, setEditProcedureRoomLoading] = useState(false);

  // Procedure States
  const [procedures, setProcedures] = useState([]);
  const [procedureLoading, setProcedureLoading] = useState(true);
  const [procedureError, setProcedureError] = useState('');
  const [showAddProcedureModal, setShowAddProcedureModal] = useState(false);
  const [newProcedureData, setNewProcedureData] = useState({
    PROCEDURE_NAME: '',
    PROCEDURE_DESCRIPTION: '',
    PROCEDURE_TYPE: 'Surgical',
    DEPARTMENT: '',
    DURATION: 1,
    COST: 0,
    INSURANCE_COVERAGE: false,
    PREPARATION_REQUIRED: '',
    RECOVERY_TIME: 0,
    RISKS: '',
    BENEFITS: '',
    CONSENT_REQUIRED: true,
    EQUIPMENT_USED: [],
    PROCEDURE_GUIDELINES: '',
    AFTERCARE_INSTRUCTIONS: '',
    FREQUENCY: '',
    AVAILABLE_DATES: [],
    SERVICE_KIND: 'Single',
    SESSION_COUNT: 1,
    SESSION_GAP_DAYS: 1,
    BILLING_MODE: 'PerSession',
    COMPONENTS: [],
  });
  const [addProcedureError, setAddProcedureError] = useState('');
  const [addProcedureLoading, setAddProcedureLoading] = useState(false);
  const [showEditProcedureModal, setShowEditProcedureModal] = useState(false);
  const [editProcedureData, setEditProcedureData] = useState(null);
  const [editProcedureError, setEditProcedureError] = useState('');
  const [editProcedureLoading, setEditProcedureLoading] = useState(false);

  // Therapist States
  const [therapists, setTherapists] = useState([]);
  const [therapistLoading, setTherapistLoading] = useState(true);
  const [therapistError, setTherapistError] = useState('');
  const [showAddTherapistModal, setShowAddTherapistModal] = useState(false);
  const [newTherapistData, setNewTherapistData] = useState({
    THERAPIST_NAME: '',
    SPECIALIZATION: '',
    QUALIFICATIONS: '',
    EXPERIENCE_YEARS: 0,
    CONTACT_NUMBER: '',
    EMAIL: '',
    CONSULTATION_HOURS: '',
    CLINIC_LOCATION: '',
    FEES: 0,
    STATUS: 'Active',
    PHOTO_ID: '',
    DEPARTMENT: '',
    BIOGRAPHY: '',
    LANGUAGES_SPOKEN: '',
    AVAILABLE_DAYS: '',
    PROCEDURES_PERFORMED: '',
    CERTIFICATIONS: '',
    FEEDBACK_SCORE: null,
  });
  const [addTherapistError, setAddTherapistError] = useState('');
  const [addTherapistLoading, setAddTherapistLoading] = useState(false);
  const [showEditTherapistModal, setShowEditTherapistModal] = useState(false);
  const [editTherapistData, setEditTherapistData] = useState(null);
  const [editTherapistError, setEditTherapistError] = useState('');
  const [editTherapistLoading, setEditTherapistLoading] = useState(false);

  // Lab Tests States
  const [labTests, setLabTests] = useState([]);
  const [labTestsLoading, setLabTestsLoading] = useState(true);
  const [labTestsError, setLabTestsError] = useState('');
  const [showAddLabTestModal, setShowAddLabTestModal] = useState(false);
  const [newLabTestData, setNewLabTestData] = useState({
    TEST_NAME: '',
    TEST_CODE: '',
    TEST_CATEGORY: 'Blood Test',
    TEST_DESCRIPTION: '',
    NORMAL_RANGE: '',
    UNIT_OF_MEASUREMENT: '',
    TEST_DURATION: 1,
    PREPARATION_REQUIRED: '',
    FASTING_REQUIRED: false,
    SAMPLE_TYPE: 'Blood',
    SAMPLE_VOLUME: '',
    COST: 0,
    INSURANCE_COVERAGE: false,
    REFERENCE_LAB: '',
    CRITICAL_VALUES: '',
    INTERPRETATION_GUIDE: '',
    STATUS: 'Active',
    REMARKS: ''
  });
  const [addLabTestError, setAddLabTestError] = useState('');
  const [addLabTestLoading, setAddLabTestLoading] = useState(false);
  const [showEditLabTestModal, setShowEditLabTestModal] = useState(false);
  const [editLabTestData, setEditLabTestData] = useState(null);
  const [editLabTestError, setEditLabTestError] = useState('');
  const [editLabTestLoading, setEditLabTestLoading] = useState(false);

  useEffect(() => {
    const fetchDoctors = async () => {
      console.log('fetchDoctors started');
      setDoctorsLoading(true);
      setDoctorsError('');
      const token = localStorage.getItem('token');
      console.log('Token:', token ? 'exists' : 'does not exist');

      if (!token) {
        setDoctorsError('Authentication token not found. Please log in again.');
        setDoctorsLoading(false);
        console.log('fetchDoctors finished: No token');
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/doctors`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (response.ok) {
          setDoctorsList(Array.isArray(data) ? data : []);
        } else {
          setDoctorsError(data.message || 'Failed to fetch doctors.');
          console.log('fetchDoctors failed:', data.message);
        }
      } catch (err) {
        setDoctorsError(`Network error: ${err.message}`);
        console.log('fetchDoctors network error:', err.message);
      } finally {
        setDoctorsLoading(false);
        console.log('setDoctorsLoading(false) called in finally');
      }
    };
    fetchDoctors();
  }, []);

  useEffect(() => {
    if (selectedTab !== 'medicines') return;

    const fetchMedicines = async () => {
      setMedicineLoading(true);
      setMedicineError('');
      const token = localStorage.getItem('token');

      if (!token) {
        setMedicineError('Authentication token not found. Please log in again.');
        setMedicineLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/medicines`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (response.ok) {
          setMedicines(Array.isArray(data) ? data : []);
        } else {
          setMedicineError(data.message || 'Failed to fetch medicines.');
        }
      } catch (err) {
        setMedicineError(`Network error: ${err.message}`);
      } finally {
        setMedicineLoading(false);
      }
    };

    fetchMedicines();
  }, [selectedTab]);

  useEffect(() => {
    if (selectedTab !== 'procedureRooms') return;

    const fetchProcedureRooms = async () => {
      setProcedureRoomLoading(true);
      setProcedureRoomError('');
      const token = localStorage.getItem('token');

      if (!token) {
        setProcedureRoomError('Authentication token not found. Please log in again.');
        setProcedureRoomLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/procedureRooms`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('API Response Error for Procedure Rooms:', errorText);
          throw new Error(errorText || 'Failed to fetch procedure rooms with unknown error.');
        }
        
        const data = await response.json();
        if (response.ok) {
          setProcedureRooms(Array.isArray(data) ? data : []);
        } else {
          setProcedureRoomError(data.message || 'Failed to fetch procedure rooms.');
        }
      } catch (err) {
        setProcedureRoomError(`Network error: ${err.message}`);
      } finally {
        setProcedureRoomLoading(false);
      }
    };

    fetchProcedureRooms();
  }, [selectedTab]);

  useEffect(() => {
    if (selectedTab !== 'procedures') return;

    const fetchProcedures = async () => {
      setProcedureLoading(true);
      setProcedureError('');
      const token = localStorage.getItem('token');

      if (!token) {
        setProcedureError('Authentication token not found. Please log in again.');
        setProcedureLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/procedures`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (response.ok) {
          setProcedures(Array.isArray(data) ? data : []);
        } else {
          setProcedureError(data.message || 'Failed to fetch procedures.');
        }
      } catch (err) {
        setProcedureError(`Network error: ${err.message}`);
      } finally {
        setProcedureLoading(false);
      }
    };

    fetchProcedures();
  }, [selectedTab]);

  useEffect(() => {
    if (selectedTab !== 'therapists') return;

    const fetchTherapists = async () => {
      setTherapistLoading(true);
      setTherapistError('');
      const token = localStorage.getItem('token');

      if (!token) {
        setTherapistError('Authentication token not found. Please log in again.');
        setTherapistLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/therapists`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (response.ok) {
          setTherapists(Array.isArray(data) ? data : []);
        } else {
          setTherapistError(data.message || 'Failed to fetch therapists.');
        }
      } catch (err) {
        setTherapistError(`Network error: ${err.message}`);
      } finally {
        setTherapistLoading(false);
      }
    };

    fetchTherapists();
  }, [selectedTab]);

  useEffect(() => {
    if (selectedTab !== 'labTests') return;

    const fetchLabTests = async () => {
      setLabTestsLoading(true);
      setLabTestsError('');
      const token = localStorage.getItem('token');

      if (!token) {
        setLabTestsError('Authentication token not found. Please log in again.');
        setLabTestsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/labTests`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Lab tests data:', data);
        console.log('Lab tests array:', data.labTests);
        console.log('Lab tests length:', data.labTests?.length || 0);
        setLabTests(data.labTests || []);
      } catch (error) {
        console.error('Error fetching lab tests:', error);
        setLabTestsError(`Failed to fetch lab tests: ${error.message}`);
      } finally {
        setLabTestsLoading(false);
      }
    };

    fetchLabTests();
  }, [selectedTab]);

  const handleAddDoctorChange = (e) => {
    const { name, value } = e.target;
    if (name === 'CONSULTATION_HOURS' || name === 'LANGUAGES_SPOKEN' || name === 'AVAILABLE_DAYS') {
      setNewDoctorFormData((prevData) => ({ ...prevData, [name]: value.split(',').map(s => s.trim()) }));
    } else {
    setNewDoctorFormData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const handleAddMedicineChange = (e) => {
    const { name, value } = e.target;
    setNewMedicineData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleAddProcedureChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewProcedureData((prevData) => {
      let newValue = value;
      if (type === 'checkbox') {
        newValue = checked;
      } else if (name === 'RECOVERY_TIME' || name === 'DURATION' || name === 'COST') {
        newValue = Number(value);
      } else if (name === 'EQUIPMENT_USED' || name === 'AVAILABLE_DATES') {
        newValue = value ? value.split(',').map(s => s.trim()) : [];
      }
      return { ...prevData, [name]: newValue };
    });
  };

  const handleAddTherapistChange = (e) => {
    const { name, value } = e.target;
    setNewTherapistData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleAddLabTestChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewLabTestData((prevData) => {
      let newValue = value;
      if (type === 'checkbox') {
        newValue = checked;
      } else if (name === 'TEST_DURATION' || name === 'COST') {
        newValue = Number(value);
      }
      return { ...prevData, [name]: newValue };
    });
  };

  const handleEditLabTestChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditLabTestData((prevData) => {
      let newValue = value;
      if (type === 'checkbox') {
        newValue = checked;
      } else if (name === 'TEST_DURATION' || name === 'COST') {
        newValue = Number(value);
      }
      return { ...prevData, [name]: newValue };
    });
  };

  const handleEditDoctorChange = (e) => {
    const { name, value } = e.target;
    setEditDoctorFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleEditMedicineChange = (e) => {
    const { name, value } = e.target;
    setEditMedicineData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleEditProcedureChange = (e) => {
    const { name, value } = e.target;
    setEditProcedureData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleEditTherapistChange = (e) => {
    const { name, value } = e.target;
    setEditTherapistData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleAddDoctorSubmit = async (e) => {
    e.preventDefault();
    setAddDoctorLoading(true);
    setAddDoctorError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setAddDoctorError('Authentication token not found. Please log in again.');
      setAddDoctorLoading(false);
      return;
    }

    // Basic validation
    if (!newDoctorFormData.DOCTOR_NAME || !newDoctorFormData.SPECIALIZATION || !newDoctorFormData.CONTACT_NUMBER || !newDoctorFormData.EMAIL) {
      setAddDoctorError('Doctor Name, Specialty, Contact Number, and Email are required.');
      setAddDoctorLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/doctors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newDoctorFormData),
      });

      const data = await response.json();

      if (response.ok) {
        setDoctorsList((prevDoctors) => [...prevDoctors, data]);
        setNewDoctorFormData({ 
          DOCTOR_NAME: '',
          SPECIALIZATION: '',
          QUALIFICATIONS: '',
          EXPERIENCE_YEARS: 0,
          CONTACT_NUMBER: '',
          EMAIL: '',
          CONSULTATION_HOURS: [],
          CLINIC_LOCATION: '',
          APPOINTMENT_FEES: 0,
          STATUS: 'Active',
          PHOTO_ID: '',
          DEPARTMENT: '',
          BIOGRAPHY: '',
          LANGUAGES_SPOKEN: [],
          AVAILABLE_DAYS: []
        });
        setShowAddDoctorModal(false);
      } else {
        setAddDoctorError(data.message || 'Failed to add doctor.');
      }
    } catch (err) {
      setAddDoctorError(`Network error: ${err.message}`);
    }
    setAddDoctorLoading(false);
  };

  const handleAddMedicineSubmit = async (e) => {
    e.preventDefault();
    setAddMedicineLoading(true);
    setAddMedicineError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setAddMedicineError('Authentication token not found. Please log in again.');
      setAddMedicineLoading(false);
      return;
    }

    // Basic validation for medicines
    if (!newMedicineData.MEDICINE_NAME || !newMedicineData.MEDICINE_TYPE || !newMedicineData.EXPIRATION_DATE) {
      setAddMedicineError('Medicine Name, Medicine Type, and Expiration Date are required.');
      setAddMedicineLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/medicines`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newMedicineData),
      });

      const data = await response.json();

      if (response.ok) {
        setMedicines((prevMedicines) => [...prevMedicines, data]);
        setNewMedicineData({
          MEDICINE_NAME: '',
          GENERIC_NAME: '',
          BRAND_NAME: '',
          MEDICINE_TYPE: 'Tablet',
          DOSAGE_FORM: '',
          STRENGTH: '',
          QUANTITY_IN_STOCK: 0,
          EXPIRATION_DATE: '',
          SUPPLIER_ID: '',
          SUPPLIER_NAME: '',
          PURCHASE_DATE: '',
          PURCHASE_PRICE: 0,
          SELLING_PRICE: 0,
          BATCH_NUMBER: '',
          STORAGE_LOCATION: '',
          REORDER_LEVEL: 0,
          REORDER_QUANTITY: 1,
          STATUS: 'Available',
          REMARKS: '',
        });
        setShowAddMedicineModal(false);
      } else {
        setAddMedicineError(data.message || 'Failed to add medicine.');
      }
    } catch (err) {
      setAddMedicineError(`Network error: ${err.message}`);
    }
    setAddMedicineLoading(false);
  };

  const handleAddProcedureRoomSubmit = async (e) => {
    e.preventDefault();
    setAddProcedureRoomLoading(true);
    setAddProcedureRoomError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setAddProcedureRoomError('Authentication token not found. Please log in again.');
      setAddProcedureRoomLoading(false);
      return;
    }

    // Basic validation for procedure rooms
    if (!newProcedureRoomData.ROOM_NAME || newProcedureRoomData.CAPACITY < 1) {
      setAddProcedureRoomError('Room Name and Capacity are required.');
      setAddProcedureRoomLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/procedureRooms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newProcedureRoomData),
      });

      const data = await response.json();

      if (response.ok) {
        setProcedureRooms((prevRooms) => [...prevRooms, data]);
        setNewProcedureRoomData({
          ROOM_NAME: '',
          LOCATION: '',
          CAPACITY: 1,
          EQUIPMENT_AVAILABLE: '',
          STATUS: 'Available',
        });
        setShowAddProcedureRoomModal(false);
      } else {
        setAddProcedureRoomError(data.message || 'Failed to add procedure room.');
      }
    } catch (err) {
      setAddProcedureRoomError(`Network error: ${err.message}`);
    }
    setAddProcedureRoomLoading(false);
  };

  const handleEditDoctorSubmit = async (e) => {
    e.preventDefault();
    setEditDoctorLoading(true);
    setEditDoctorError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setEditDoctorError('Authentication token not found. Please log in again.');
      setEditDoctorLoading(false);
      return;
    }

    if (!editDoctorFormData.DOCTOR_NAME || !editDoctorFormData.SPECIALIZATION || !editDoctorFormData.CONTACT_NUMBER || !editDoctorFormData.EMAIL) {
      setEditDoctorError('Doctor Name, Specialty, Contact Number, and Email are required.');
      setEditDoctorLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/doctors/${editDoctorFormData._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(editDoctorFormData),
      });

      const data = await response.json();

      if (response.ok) {
        setDoctorsList((prevDoctors) =>
          prevDoctors.map((doc) => (doc._id === editDoctorFormData._id ? data : doc))
        );
        setEditDoctorFormData(null);
        setShowEditDoctorModal(false);
      } else {
        setEditDoctorError(data.message || 'Failed to update doctor.');
      }
    } catch (err) {
      setEditDoctorError(`Network error: ${err.message}`);
    }
    setEditDoctorLoading(false);
  };

  const handleEditMedicineSubmit = async (e) => {
    e.preventDefault();
    setEditMedicineLoading(true);
    setEditMedicineError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setEditMedicineError('Authentication token not found. Please log in again.');
      setEditMedicineLoading(false);
      return;
    }

    if (!editMedicineData.MEDICINE_NAME || !editMedicineData.MEDICINE_TYPE || !editMedicineData.EXPIRATION_DATE) {
      setEditMedicineError('Medicine Name, Medicine Type, and Expiration Date are required.');
      setEditMedicineLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/medicines/${editMedicineData._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(editMedicineData),
      });

      const data = await response.json();

      if (response.ok) {
        setMedicines((prevMedicines) =>
          prevMedicines.map((med) => (med._id === editMedicineData._id ? data : med))
        );
        setEditMedicineData(null);
        setShowEditMedicineModal(false);
      } else {
        setEditMedicineError(data.message || 'Failed to update medicine.');
      }
    } catch (err) {
      setEditMedicineError(`Network error: ${err.message}`);
    }
    setEditMedicineLoading(false);
  };

  const handleEditProcedureRoomSubmit = async (e) => {
    e.preventDefault();
    setEditProcedureRoomLoading(true);
    setEditProcedureRoomError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setEditProcedureRoomError('Authentication token not found. Please log in again.');
      setEditProcedureRoomLoading(false);
      return;
    }

    if (!editProcedureRoomData.ROOM_NAME || editProcedureRoomData.CAPACITY < 1) {
      setEditProcedureRoomError('Room Name and Capacity are required.');
      setEditProcedureRoomLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/procedureRooms/${editProcedureRoomData._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(editProcedureRoomData),
      });

      const data = await response.json();

      if (response.ok) {
        setProcedureRooms((prevRooms) =>
          prevRooms.map((room) => (room._id === editProcedureRoomData._id ? data : room))
        );
        setEditProcedureRoomData(null);
        setShowEditProcedureRoomModal(false);
      } else {
        setEditProcedureRoomError(data.message || 'Failed to update procedure room.');
      }
    } catch (err) {
      setEditProcedureRoomError(`Network error: ${err.message}`);
    }
    setEditProcedureRoomLoading(false);
  };

  const handleAddTherapistSubmit = async (e) => {
    e.preventDefault();
    setAddTherapistLoading(true);
    setAddTherapistError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setAddTherapistError('Authentication token not found. Please log in again.');
      setAddTherapistLoading(false);
      return;
    }

    // Basic validation
    if (!newTherapistData.THERAPIST_NAME || !newTherapistData.SPECIALIZATION || !newTherapistData.CONTACT_NUMBER || !newTherapistData.EMAIL) {
      setAddTherapistError('Therapist Name, Specialty, Contact Number, and Email are required.');
      setAddTherapistLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/therapists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newTherapistData),
      });

      const data = await response.json();

      if (response.ok) {
        setTherapists((prevTherapists) => [...prevTherapists, data]);
        setNewTherapistData({
          THERAPIST_NAME: '',
          SPECIALIZATION: '',
          QUALIFICATIONS: '',
          EXPERIENCE_YEARS: 0,
          CONTACT_NUMBER: '',
          EMAIL: '',
          CONSULTATION_HOURS: '',
          CLINIC_LOCATION: '',
          FEES: 0,
          STATUS: 'Active',
          PHOTO_ID: '',
          DEPARTMENT: '',
          BIOGRAPHY: '',
          LANGUAGES_SPOKEN: '',
          AVAILABLE_DAYS: '',
          PROCEDURES_PERFORMED: '',
          CERTIFICATIONS: '',
          FEEDBACK_SCORE: null,
        });
        setShowAddTherapistModal(false);
      } else {
        setAddTherapistError(data.message || 'Failed to add therapist.');
      }
    } catch (err) {
      setAddTherapistError(`Network error: ${err.message}`);
    }
    setAddTherapistLoading(false);
  };

  const handleAddLabTestSubmit = async (e) => {
    e.preventDefault();
    setAddLabTestLoading(true);
    setAddLabTestError('');
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_BASE_URL}/labTests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newLabTestData),
      });

      const data = await response.json();

      if (response.ok) {
        setLabTests([...labTests, data.labTest]);
        setNewLabTestData({
          TEST_NAME: '',
          TEST_CODE: '',
          TEST_CATEGORY: 'Blood Test',
          TEST_DESCRIPTION: '',
          NORMAL_RANGE: '',
          UNIT_OF_MEASUREMENT: '',
          TEST_DURATION: 1,
          PREPARATION_REQUIRED: '',
          FASTING_REQUIRED: false,
          SAMPLE_TYPE: 'Blood',
          SAMPLE_VOLUME: '',
          COST: 0,
          INSURANCE_COVERAGE: false,
          REFERENCE_LAB: '',
          CRITICAL_VALUES: '',
          INTERPRETATION_GUIDE: '',
          STATUS: 'Active',
          REMARKS: ''
        });
        setShowAddLabTestModal(false);
      } else {
        setAddLabTestError(data.message || 'Failed to add lab test.');
      }
    } catch (err) {
      setAddLabTestError(`Network error: ${err.message}`);
    }
    setAddLabTestLoading(false);
  };

  const handleEditLabTestSubmit = async (e) => {
    e.preventDefault();
    setEditLabTestLoading(true);
    setEditLabTestError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setEditLabTestError('Authentication token not found. Please log in again.');
      setEditLabTestLoading(false);
      return;
    }

    const id = editLabTestData.LAB_TEST_ID || editLabTestData._id;

    try {
      const response = await fetch(`${API_BASE_URL}/labTests/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(editLabTestData),
      });

      const data = await response.json();

      if (response.ok) {
        const updated = data.labTest || data;
        setLabTests((prev) => prev.map((test) => (test._id === updated._id ? updated : test)));
        setEditLabTestData(null);
        setShowEditLabTestModal(false);
      } else {
        setEditLabTestError(data.message || 'Failed to update lab test.');
      }
    } catch (err) {
      setEditLabTestError(`Network error: ${err.message}`);
    }
    setEditLabTestLoading(false);
  };

  const handleDeleteDoctor = async (id) => {
    if (!window.confirm('Are you sure you want to delete this doctor?')) {
      return;
    }

    setDoctorsLoading(true); // Set loading to true while deleting to prevent multiple deletions
    setDoctorsError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setDoctorsError('Authentication token not found. Please log in again.');
      setDoctorsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/doctors/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setDoctorsList((prevDoctors) => prevDoctors.filter((doc) => doc._id !== id));
      } else {
        const data = await response.json();
        setDoctorsError(data.message || 'Failed to delete doctor.');
      }
    } catch (err) {
      setDoctorsError(`Network error: ${err.message}`);
    }
    setDoctorsLoading(false);
  };

  const handleDeleteMedicine = async (id) => {
    if (!window.confirm('Are you sure you want to delete this medicine?')) {
      return;
    }

    setMedicineLoading(true);
    setMedicineError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setMedicineError('Authentication token not found. Please log in again.');
      setMedicineLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/medicines/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMedicines((prevMedicines) => prevMedicines.filter((med) => med._id !== id));
      } else {
        const data = await response.json();
        setMedicineError(data.message || 'Failed to delete medicine.');
      }
    } catch (err) {
      setMedicineError(`Network error: ${err.message}`);
    }
    setMedicineLoading(false);
  };

  const handleDeleteProcedureRoom = async (id) => {
    if (!window.confirm('Are you sure you want to delete this procedure room?')) {
      return;
    }

    setProcedureRoomLoading(true);
    setProcedureRoomError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setProcedureRoomError('Authentication token not found. Please log in again.');
      setProcedureRoomLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/procedureRooms/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setProcedureRooms((prevRooms) => prevRooms.filter((room) => room._id !== id));
      } else {
        const data = await response.json();
        setProcedureRoomError(data.message || 'Failed to delete procedure room.');
      }
    } catch (err) {
      setProcedureRoomError(`Network error: ${err.message}`);
    }
    setProcedureRoomLoading(false);
  };

  const handleAddProcedureSubmit = async (e) => {
    e.preventDefault();
    setAddProcedureLoading(true);
    setAddProcedureError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setAddProcedureError('Authentication token not found. Please log in again.');
      setAddProcedureLoading(false);
      return;
    }

    // Basic validation for procedures
    if (!newProcedureData.PROCEDURE_NAME || !newProcedureData.PROCEDURE_DESCRIPTION || !newProcedureData.DEPARTMENT || !newProcedureData.DURATION || !newProcedureData.COST) {
      setAddProcedureError('Procedure Name, Description, Department, Duration, and Cost are required.');
      setAddProcedureLoading(false);
      return;
    }

    console.log("Submitting new procedure data:", newProcedureData);
    try {
      const response = await fetch(`${API_BASE_URL}/procedures`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newProcedureData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Response Error for Add Procedure:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          setAddProcedureError(errorData.message || 'Failed to add procedure with unknown error.');
        } catch (parseError) {
          setAddProcedureError(`Failed to add procedure. Server responded with: ${errorText}`);
        }
        setAddProcedureLoading(false);
        return;
      }

      const data = await response.json();

      if (response.ok) {
        setProcedures((prevProcedures) => [...prevProcedures, data]);
        setNewProcedureData({
          PROCEDURE_NAME: '',
          PROCEDURE_DESCRIPTION: '',
          PROCEDURE_TYPE: 'Surgical',
          DEPARTMENT: '',
          DURATION: 1,
          COST: 0,
          INSURANCE_COVERAGE: false,
          PREPARATION_REQUIRED: '',
          RECOVERY_TIME: 0,
          RISKS: '',
          BENEFITS: '',
          CONSENT_REQUIRED: true,
          EQUIPMENT_USED: [],
          PROCEDURE_GUIDELINES: '',
          AFTERCARE_INSTRUCTIONS: '',
          FREQUENCY: '',
          AVAILABLE_DATES: [],
          SERVICE_KIND: 'Single',
          SESSION_COUNT: 1,
          SESSION_GAP_DAYS: 1,
          BILLING_MODE: 'PerSession',
          COMPONENTS: [],
        });
        setShowAddProcedureModal(false);
      } else {
        setAddProcedureError(data.message || 'Failed to add procedure.');
      }
    } catch (err) {
      setAddProcedureError(`Network error: ${err.message}`);
    }
    setAddProcedureLoading(false);
  };

  const handleEditProcedureSubmit = async (e) => {
    e.preventDefault();
    setEditProcedureLoading(true);
    setEditProcedureError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setEditProcedureError('Authentication token not found. Please log in again.');
      setEditProcedureLoading(false);
      return;
    }

    if (!editProcedureData.PROCEDURE_NAME || !editProcedureData.PROCEDURE_DESCRIPTION || !editProcedureData.DEPARTMENT || !editProcedureData.DURATION || !editProcedureData.COST) {
      setEditProcedureError('Procedure Name, Description, Department, Duration, and Cost are required.');
      setEditProcedureLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/procedures/${editProcedureData._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(editProcedureData),
      });

      const data = await response.json();

      if (response.ok) {
        setProcedures((prevProcedures) =>
          prevProcedures.map((proc) => (proc._id === editProcedureData._id ? data : proc))
        );
        setEditProcedureData(null);
        setShowEditProcedureModal(false);
      } else {
        setEditProcedureError(data.message || 'Failed to update procedure.');
      }
    } catch (err) {
      setEditProcedureError(`Network error: ${err.message}`);
    }
    setEditProcedureLoading(false);
  };

  const handleDeleteProcedure = async (id) => {
    if (!window.confirm('Are you sure you want to delete this procedure?')) {
      return;
    }

    setProcedureLoading(true);
    setProcedureError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setProcedureError('Authentication token not found. Please log in again.');
      setProcedureLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/procedures/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setProcedures((prevProcedures) => prevProcedures.filter((proc) => proc._id !== id));
      } else {
        const data = await response.json();
        setProcedureError(data.message || 'Failed to delete procedure.');
      }
    } catch (err) {
      setProcedureError(`Network error: ${err.message}`);
    }
    setProcedureLoading(false);
  };

  const handleDeleteTherapist = async (id) => {
    if (!window.confirm('Are you sure you want to delete this therapist?')) {
      return;
    }

    setTherapistLoading(true);
    setTherapistError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setTherapistError('Authentication token not found. Please log in again.');
      setTherapistLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/therapists/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setTherapists((prevTherapists) => prevTherapists.filter((therapist) => therapist._id !== id));
      } else {
        const data = await response.json();
        setTherapistError(data.message || 'Failed to delete therapist.');
      }
    } catch (err) {
      setTherapistError(`Network error: ${err.message}`);
    }
    setTherapistLoading(false);
  };

  const handleEditTherapistSubmit = async (e) => {
    e.preventDefault();
    setEditTherapistLoading(true);
    setEditTherapistError('');
    const token = localStorage.getItem('token');

    if (!token) {
      setEditTherapistError('Authentication token not found. Please log in again.');
      setEditTherapistLoading(false);
      return;
    }

    if (!editTherapistData.THERAPIST_NAME || !editTherapistData.SPECIALIZATION || !editTherapistData.CONTACT_NUMBER || !editTherapistData.EMAIL) {
      setEditTherapistError('Therapist Name, Specialty, Contact Number, and Email are required.');
      setEditTherapistLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/therapists/${editTherapistData._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(editTherapistData),
      });

      const data = await response.json();

      if (response.ok) {
        setTherapists((prevTherapists) =>
          prevTherapists.map((therapist) => (therapist._id === editTherapistData._id ? data : therapist))
        );
        setEditTherapistData(null);
        setShowEditTherapistModal(false);
      } else {
        setEditTherapistError(data.message || 'Failed to update therapist.');
      }
    } catch (err) {
      setEditTherapistError(`Network error: ${err.message}`);
    }
    setEditTherapistLoading(false);
  };

  const handleAddProcedureRoomChange = (e) => {
    const { name, value } = e.target;
    setNewProcedureRoomData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleEditProcedureRoomChange = (e) => {
    const { name, value } = e.target;
    setEditProcedureRoomData((prevData) => ({ ...prevData, [name]: value }));
  };

  if (doctorsLoading && selectedTab === 'doctors') {
    return <div style={{ maxWidth: 900, margin: '2rem auto' }}>Loading doctors data...</div>;
  }
  if (medicineLoading && selectedTab === 'medicines') {
    return <div style={{ maxWidth: 900, margin: '2rem auto' }}>Loading medicines data...</div>;
  }
  if (procedureRoomLoading && selectedTab === 'procedureRooms') {
    return <div style={{ maxWidth: 900, margin: '2rem auto' }}>Loading procedure rooms data...</div>;
  }
  if (procedureLoading && selectedTab === 'procedures') {
    return <div style={{ maxWidth: 900, margin: '2rem auto' }}>Loading procedures data...</div>;
  }
  if (therapistLoading && selectedTab === 'therapists') {
    return <div style={{ maxWidth: 900, margin: '2rem auto' }}>Loading therapists data...</div>;
  }
  if (labTestsLoading && selectedTab === 'labTests') {
    return <div style={{ maxWidth: 900, margin: '2rem auto' }}>Loading lab tests data...</div>;
  }

  if (doctorsError && selectedTab === 'doctors') {
    return <div style={{ maxWidth: 900, margin: '2rem auto', color: 'red' }}>Error: {doctorsError}</div>;
  }

  if (medicineError && selectedTab === 'medicines') {
    return <div style={{ maxWidth: 900, margin: '2rem auto', color: 'red' }}>Error: {medicineError}</div>;
  }

  if (procedureRoomError && selectedTab === 'procedureRooms') {
    return <div style={{ maxWidth: 900, margin: '2rem auto', color: 'red' }}>Error: {procedureRoomError}</div>;
  }

  if (procedureError && selectedTab === 'procedures') {
    return <div style={{ maxWidth: 900, margin: '2rem auto', color: 'red' }}>Error: {procedureError}</div>;
  }

  if (therapistError && selectedTab === 'therapists') {
    return <div style={{ maxWidth: 900, margin: '2rem auto', color: 'red' }}>Error: {therapistError}</div>;
  }
  if (labTestsError && selectedTab === 'labTests') {
    return <div style={{ maxWidth: 900, margin: '2rem auto', color: 'red' }}>Error: {labTestsError}</div>;
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2rem' }}>
      <h2>Reference Data Management</h2>
      
      {/* Navigation Tabs */}
      <div style={{ 
        marginBottom: '2rem', 
        borderBottom: '2px solid #e9ecef', 
        paddingBottom: '0.5rem',
        display: 'flex',
        gap: '0.5rem',
        flexWrap: 'wrap'
      }}>
        <button 
          onClick={() => setSelectedTab('doctors')}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: selectedTab === 'doctors' ? '#007bff' : 'transparent',
            color: selectedTab === 'doctors' ? 'white' : '#6c757d',
            border: selectedTab === 'doctors' ? 'none' : '1px solid #ddd',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: selectedTab === 'doctors' ? 'bold' : 'normal',
            transition: 'all 0.3s ease',
            borderBottom: selectedTab === 'doctors' ? '3px solid #007bff' : '3px solid transparent'
          }}
          onMouseEnter={(e) => {
            if (selectedTab !== 'doctors') {
              e.target.style.backgroundColor = '#007bff20';
              e.target.style.color = '#007bff';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedTab !== 'doctors') {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#6c757d';
            }
          }}
        >
          👨‍⚕️ Doctors
        </button>
        <button 
          onClick={() => setSelectedTab('medicines')}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: selectedTab === 'medicines' ? '#28a745' : 'transparent',
            color: selectedTab === 'medicines' ? 'white' : '#6c757d',
            border: selectedTab === 'medicines' ? 'none' : '1px solid #ddd',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: selectedTab === 'medicines' ? 'bold' : 'normal',
            transition: 'all 0.3s ease',
            borderBottom: selectedTab === 'medicines' ? '3px solid #28a745' : '3px solid transparent'
          }}
          onMouseEnter={(e) => {
            if (selectedTab !== 'medicines') {
              e.target.style.backgroundColor = '#28a74520';
              e.target.style.color = '#28a745';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedTab !== 'medicines') {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#6c757d';
            }
          }}
        >
          💊 Medicines
        </button>
        <button 
          onClick={() => setSelectedTab('procedureRooms')}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: selectedTab === 'procedureRooms' ? '#ffc107' : 'transparent',
            color: selectedTab === 'procedureRooms' ? '#212529' : '#6c757d',
            border: selectedTab === 'procedureRooms' ? 'none' : '1px solid #ddd',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: selectedTab === 'procedureRooms' ? 'bold' : 'normal',
            transition: 'all 0.3s ease',
            borderBottom: selectedTab === 'procedureRooms' ? '3px solid #ffc107' : '3px solid transparent'
          }}
          onMouseEnter={(e) => {
            if (selectedTab !== 'procedureRooms') {
              e.target.style.backgroundColor = '#ffc10720';
              e.target.style.color = '#ffc107';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedTab !== 'procedureRooms') {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#6c757d';
            }
          }}
        >
          🏥 Procedure Rooms
        </button>
        <button 
          onClick={() => setSelectedTab('procedures')}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: selectedTab === 'procedures' ? '#17a2b8' : 'transparent',
            color: selectedTab === 'procedures' ? 'white' : '#6c757d',
            border: selectedTab === 'procedures' ? 'none' : '1px solid #ddd',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: selectedTab === 'procedures' ? 'bold' : 'normal',
            transition: 'all 0.3s ease',
            borderBottom: selectedTab === 'procedures' ? '3px solid #17a2b8' : '3px solid transparent'
          }}
          onMouseEnter={(e) => {
            if (selectedTab !== 'procedures') {
              e.target.style.backgroundColor = '#17a2b820';
              e.target.style.color = '#17a2b8';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedTab !== 'procedures') {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#6c757d';
            }
          }}
        >
          ⚕️ Procedures
        </button>
        <button 
          onClick={() => setSelectedTab('therapists')}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: selectedTab === 'therapists' ? '#6f42c1' : 'transparent',
            color: selectedTab === 'therapists' ? 'white' : '#6c757d',
            border: selectedTab === 'therapists' ? 'none' : '1px solid #ddd',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: selectedTab === 'therapists' ? 'bold' : 'normal',
            transition: 'all 0.3s ease',
            borderBottom: selectedTab === 'therapists' ? '3px solid #6f42c1' : '3px solid transparent'
          }}
          onMouseEnter={(e) => {
            if (selectedTab !== 'therapists') {
              e.target.style.backgroundColor = '#6f42c120';
              e.target.style.color = '#6f42c1';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedTab !== 'therapists') {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#6c757d';
            }
          }}
        >
          🏥 Therapists
        </button>
        <button 
          onClick={() => setSelectedTab('labTests')}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: selectedTab === 'labTests' ? '#dc3545' : 'transparent',
            color: selectedTab === 'labTests' ? 'white' : '#6c757d',
            border: selectedTab === 'labTests' ? 'none' : '1px solid #ddd',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: selectedTab === 'labTests' ? 'bold' : 'normal',
            transition: 'all 0.3s ease',
            borderBottom: selectedTab === 'labTests' ? '3px solid #dc3545' : '3px solid transparent'
          }}
          onMouseEnter={(e) => {
            if (selectedTab !== 'labTests') {
              e.target.style.backgroundColor = '#dc354520';
              e.target.style.color = '#dc3545';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedTab !== 'labTests') {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#6c757d';
            }
          }}
        >
          🧪 Lab Tests
        </button>
      </div>

      {selectedTab === 'doctors' && (
        <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, color: '#495057' }}>👨‍⚕️ Doctors Management</h3>
            <button 
              onClick={() => setShowAddDoctorModal(true)} 
              style={{ 
                padding: '0.5rem 1rem', 
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Add Doctor
            </button>
          </div>

          {/* Add Doctor Modal */}
          {showAddDoctorModal && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ 
                background: '#fff', 
                padding: '2rem', 
                borderRadius: '8px', 
                minWidth: 400, 
                maxHeight: '90vh', 
                overflowY: 'auto',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                border: '1px solid #e9ecef'
              }}>
                <h3 style={{ margin: '0 0 1.5rem 0', color: '#495057', borderBottom: '2px solid #007bff', paddingBottom: '0.5rem' }}>Add New Doctor</h3>
                <form onSubmit={handleAddDoctorSubmit}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#495057' }}>Doctor Name:</label>
                    <input 
                      type="text" 
                      name="DOCTOR_NAME" 
                      value={newDoctorFormData.DOCTOR_NAME} 
                      onChange={handleAddDoctorChange} 
                      required 
                      style={{ 
                        width: '100%', 
                        padding: '0.75rem', 
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '1rem',
                        boxSizing: 'border-box',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#007bff'}
                      onBlur={(e) => e.target.style.borderColor = '#ddd'}
                    />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Specialization:</label><br />
                    <input type="text" name="SPECIALIZATION" value={newDoctorFormData.SPECIALIZATION} onChange={handleAddDoctorChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Qualifications:</label><br />
                    <input type="text" name="QUALIFICATIONS" value={newDoctorFormData.QUALIFICATIONS} onChange={handleAddDoctorChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Experience (Years):</label><br />
                    <input type="number" name="EXPERIENCE_YEARS" value={newDoctorFormData.EXPERIENCE_YEARS} onChange={handleAddDoctorChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Contact Number:</label><br />
                    <input type="tel" name="CONTACT_NUMBER" value={newDoctorFormData.CONTACT_NUMBER} onChange={handleAddDoctorChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Email:</label><br />
                    <input type="email" name="EMAIL" value={newDoctorFormData.EMAIL} onChange={handleAddDoctorChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Consultation Hours (comma-separated):</label><br />
                    <input type="text" name="CONSULTATION_HOURS" value={newDoctorFormData.CONSULTATION_HOURS.join(',')} onChange={(e) => setNewDoctorFormData({ ...newDoctorFormData, CONSULTATION_HOURS: e.target.value.split(',').map(s => s.trim()) })} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Clinic Location:</label><br />
                    <input type="text" name="CLINIC_LOCATION" value={newDoctorFormData.CLINIC_LOCATION} onChange={handleAddDoctorChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Appointment Fees:</label><br />
                    <input type="number" name="APPOINTMENT_FEES" value={newDoctorFormData.APPOINTMENT_FEES} onChange={handleAddDoctorChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Status:</label><br />
                    <select name="STATUS" value={newDoctorFormData.STATUS} onChange={handleAddDoctorChange} style={{ width: '100%', padding: '0.5rem' }}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="On Leave">On Leave</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Photo ID:</label><br />
                    <input type="text" name="PHOTO_ID" value={newDoctorFormData.PHOTO_ID} onChange={handleAddDoctorChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Department:</label><br />
                    <input type="text" name="DEPARTMENT" value={newDoctorFormData.DEPARTMENT} onChange={handleAddDoctorChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Biography:</label><br />
                    <textarea name="BIOGRAPHY" value={newDoctorFormData.BIOGRAPHY} onChange={handleAddDoctorChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Languages Spoken (comma-separated):</label><br />
                    <input type="text" name="LANGUAGES_SPOKEN" value={newDoctorFormData.LANGUAGES_SPOKEN ? newDoctorFormData.LANGUAGES_SPOKEN.join(',') : ''} onChange={(e) => setNewDoctorFormData({ ...newDoctorFormData, LANGUAGES_SPOKEN: e.target.value.split(',').map(s => s.trim()) })} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Available Days (comma-separated: Monday, Tuesday, etc.):</label><br />
                    <input type="text" name="AVAILABLE_DAYS" value={newDoctorFormData.AVAILABLE_DAYS ? newDoctorFormData.AVAILABLE_DAYS.join(',') : ''} onChange={(e) => setNewDoctorFormData({ ...newDoctorFormData, AVAILABLE_DAYS: e.target.value.split(',').map(s => s.trim()) })} style={{ width: '100%' }} />
                  </div>
                  {addDoctorError && <p style={{ color: 'red' }}>{addDoctorError}</p>}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <button type="button" onClick={() => setShowAddDoctorModal(false)} disabled={addDoctorLoading}>Cancel</button>
                    <button type="submit" disabled={addDoctorLoading}>{addDoctorLoading ? 'Adding...' : 'Add Doctor'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Doctors Table */}
          {!doctorsLoading && !doctorsError && doctorsList.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', marginTop: '1rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '0.75rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Doctor Name</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.75rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Specialization</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.75rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Qualifications</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.75rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Experience (Years)</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.75rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Contact Number</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.75rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Email</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.75rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Consultation Hours</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.75rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Clinic Location</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.75rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Appointment Fees</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.75rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Status</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.75rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Department</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.75rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Languages Spoken</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.75rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Available Days</th>
                      <th style={{ border: '1px solid #ddd', padding: '0.75rem', textAlign: 'left', fontWeight: 'bold', color: '#495057' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doctorsList.map((doctor) => (
                      <tr key={doctor._id}>
                        <td style={{ border: '1px solid #ddd', padding: '0.75rem' }}>{doctor.DOCTOR_NAME}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.75rem' }}>{doctor.SPECIALIZATION}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.75rem' }}>{doctor.QUALIFICATIONS}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.75rem' }}>{doctor.EXPERIENCE_YEARS}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.75rem' }}>{doctor.CONTACT_NUMBER}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.75rem' }}>{doctor.EMAIL}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.75rem' }}>{doctor.CONSULTATION_HOURS ? doctor.CONSULTATION_HOURS.join(', ') : 'N/A'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.75rem' }}>{doctor.CLINIC_LOCATION}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.75rem' }}>{doctor.APPOINTMENT_FEES}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.75rem' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            backgroundColor: doctor.STATUS === 'Active' ? '#d4edda' : '#f8d7da',
                            color: doctor.STATUS === 'Active' ? '#155724' : '#721c24'
                          }}>
                            {doctor.STATUS}
                          </span>
                        </td>
                        <td style={{ border: '1px solid #ddd', padding: '0.75rem' }}>{doctor.DEPARTMENT}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.75rem' }}>{doctor.LANGUAGES_SPOKEN ? doctor.LANGUAGES_SPOKEN.join(', ') : 'N/A'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.75rem' }}>{doctor.AVAILABLE_DAYS ? doctor.AVAILABLE_DAYS.join(', ') : 'N/A'}</td>
                        <td style={{ border: '1px solid #ddd', padding: '0.75rem' }}>
                          <button 
                            style={{ 
                              marginRight: '0.5rem',
                              padding: '4px 8px',
                              backgroundColor: '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }} 
                            onClick={() => {
                              setEditDoctorFormData(doctor);
                              setShowEditDoctorModal(true);
                            }}
                          >
                            Edit
                          </button>
                          <button 
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                            onClick={() => handleDeleteDoctor(doctor._id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {!doctorsLoading && !doctorsError && doctorsList.length === 0 && <p>No doctors found.</p>}

          {/* Edit Doctor Modal */}
          {showEditDoctorModal && editDoctorFormData && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', padding: '2rem', borderRadius: 8, minWidth: 350, maxHeight: '90vh', overflowY: 'auto' }}>
                <h3>Edit Doctor</h3>
                <form onSubmit={handleEditDoctorSubmit}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Doctor Name:</label><br />
                    <input type="text" name="DOCTOR_NAME" value={editDoctorFormData.DOCTOR_NAME || ''} onChange={handleEditDoctorChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Specialization:</label><br />
                    <input type="text" name="SPECIALIZATION" value={editDoctorFormData.SPECIALIZATION || ''} onChange={handleEditDoctorChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Qualifications:</label><br />
                    <input type="text" name="QUALIFICATIONS" value={editDoctorFormData.QUALIFICATIONS || ''} onChange={handleEditDoctorChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Experience (Years):</label><br />
                    <input type="number" name="EXPERIENCE_YEARS" value={editDoctorFormData.EXPERIENCE_YEARS || 0} onChange={handleEditDoctorChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Contact Number:</label><br />
                    <input type="tel" name="CONTACT_NUMBER" value={editDoctorFormData.CONTACT_NUMBER || ''} onChange={handleEditDoctorChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Email:</label><br />
                    <input type="email" name="EMAIL" value={editDoctorFormData.EMAIL || ''} onChange={handleEditDoctorChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Consultation Hours (comma-separated):</label><br />
                    <input type="text" name="CONSULTATION_HOURS" value={editDoctorFormData.CONSULTATION_HOURS ? editDoctorFormData.CONSULTATION_HOURS.join(',') : ''} onChange={(e) => setEditDoctorFormData({ ...editDoctorFormData, CONSULTATION_HOURS: e.target.value.split(',').map(s => s.trim()) })} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Clinic Location:</label><br />
                    <input type="text" name="CLINIC_LOCATION" value={editDoctorFormData.CLINIC_LOCATION || ''} onChange={handleEditDoctorChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Appointment Fees:</label><br />
                    <input type="number" name="APPOINTMENT_FEES" value={editDoctorFormData.APPOINTMENT_FEES || 0} onChange={handleEditDoctorChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Status:</label><br />
                    <select name="STATUS" value={editDoctorFormData.STATUS || 'Active'} onChange={handleEditDoctorChange} style={{ width: '100%', padding: '0.5rem' }}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="On Leave">On Leave</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Photo ID:</label><br />
                    <input type="text" name="PHOTO_ID" value={editDoctorFormData.PHOTO_ID || ''} onChange={handleEditDoctorChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Department:</label><br />
                    <input type="text" name="DEPARTMENT" value={editDoctorFormData.DEPARTMENT || ''} onChange={handleEditDoctorChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Biography:</label><br />
                    <textarea name="BIOGRAPHY" value={editDoctorFormData.BIOGRAPHY || ''} onChange={handleEditDoctorChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Languages Spoken (comma-separated):</label><br />
                    <input type="text" name="LANGUAGES_SPOKEN" value={editDoctorFormData.LANGUAGES_SPOKEN ? editDoctorFormData.LANGUAGES_SPOKEN.join(',') : ''} onChange={(e) => setEditDoctorFormData({ ...editDoctorFormData, LANGUAGES_SPOKEN: e.target.value.split(',').map(s => s.trim()) })} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Available Days (comma-separated: Monday, Tuesday, etc.):</label><br />
                    <input type="text" name="AVAILABLE_DAYS" value={editDoctorFormData.AVAILABLE_DAYS ? editDoctorFormData.AVAILABLE_DAYS.join(',') : ''} onChange={(e) => setEditDoctorFormData({ ...editDoctorFormData, AVAILABLE_DAYS: e.target.value.split(',').map(s => s.trim()) })} style={{ width: '100%' }} />
                  </div>
                  {editDoctorError && <p style={{ color: 'red' }}>{editDoctorError}</p>}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <button type="button" onClick={() => setShowEditDoctorModal(false)} disabled={editDoctorLoading}>Cancel</button>
                    <button type="submit" disabled={editDoctorLoading}>{editDoctorLoading ? 'Saving...' : 'Save Changes'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedTab === 'medicines' && (
        <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, color: '#495057' }}>💊 Medicines Management</h3>
            <button 
              onClick={() => setShowAddMedicineModal(true)} 
              style={{ 
                padding: '0.5rem 1rem', 
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Add Medicine
            </button>
          </div>

          {/* Add Medicine Modal */}
          {showAddMedicineModal && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', padding: '2rem', borderRadius: 8, minWidth: 350, maxHeight: '90vh', overflowY: 'auto' }}>
                <h3>Add New Medicine</h3>
                <form onSubmit={handleAddMedicineSubmit}>
                  {/* Medicine Fields */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Medicine Name:</label><br />
                    <input type="text" name="MEDICINE_NAME" value={newMedicineData.MEDICINE_NAME} onChange={handleAddMedicineChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Generic Name:</label><br />
                    <input type="text" name="GENERIC_NAME" value={newMedicineData.GENERIC_NAME} onChange={handleAddMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Brand Name:</label><br />
                    <input type="text" name="BRAND_NAME" value={newMedicineData.BRAND_NAME} onChange={handleAddMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Medicine Type:</label><br />
                    <select name="MEDICINE_TYPE" value={newMedicineData.MEDICINE_TYPE} onChange={handleAddMedicineChange} required style={{ width: '100%', padding: '0.5rem' }}>
                      <option value="Tablet">Tablet</option>
                      <option value="Capsule">Capsule</option>
                      <option value="Syrup">Syrup</option>
                      <option value="Injection">Injection</option>
                      <option value="Ointment">Ointment</option>
                      <option value="Drops">Drops</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Dosage Form:</label><br />
                    <input type="text" name="DOSAGE_FORM" value={newMedicineData.DOSAGE_FORM} onChange={handleAddMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Strength:</label><br />
                    <input type="text" name="STRENGTH" value={newMedicineData.STRENGTH} onChange={handleAddMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Quantity In Stock:</label><br />
                    <input type="number" name="QUANTITY_IN_STOCK" value={newMedicineData.QUANTITY_IN_STOCK} onChange={handleAddMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Expiration Date:</label><br />
                    <input type="date" name="EXPIRATION_DATE" value={newMedicineData.EXPIRATION_DATE} onChange={handleAddMedicineChange} required style={{ width: '100%' }} />
                  </div>
                  {/* Assuming SUPPLIER_ID will be handled separately or looked up, for now using string input for SUPPLIER_NAME */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Supplier Name:</label><br />
                    <input type="text" name="SUPPLIER_NAME" value={newMedicineData.SUPPLIER_NAME} onChange={handleAddMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Purchase Date:</label><br />
                    <input type="date" name="PURCHASE_DATE" value={newMedicineData.PURCHASE_DATE} onChange={handleAddMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Purchase Price:</label><br />
                    <input type="number" name="PURCHASE_PRICE" value={newMedicineData.PURCHASE_PRICE} onChange={handleAddMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Selling Price:</label><br />
                    <input type="number" name="SELLING_PRICE" value={newMedicineData.SELLING_PRICE} onChange={handleAddMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Batch Number:</label><br />
                    <input type="text" name="BATCH_NUMBER" value={newMedicineData.BATCH_NUMBER} onChange={handleAddMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Storage Location:</label><br />
                    <input type="text" name="STORAGE_LOCATION" value={newMedicineData.STORAGE_LOCATION} onChange={handleAddMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Reorder Level:</label><br />
                    <input type="number" name="REORDER_LEVEL" value={newMedicineData.REORDER_LEVEL} onChange={handleAddMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Reorder Quantity:</label><br />
                    <input type="number" name="REORDER_QUANTITY" value={newMedicineData.REORDER_QUANTITY} onChange={handleAddMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Status:</label><br />
                    <select name="STATUS" value={newMedicineData.STATUS} onChange={handleAddMedicineChange} style={{ width: '100%', padding: '0.5rem' }}>
                      <option value="Available">Available</option>
                      <option value="Out of Stock">Out of Stock</option>
                      <option value="Expired">Expired</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Remarks:</label><br />
                    <textarea name="REMARKS" value={newMedicineData.REMARKS} onChange={handleAddMedicineChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>

                  {addMedicineError && <p style={{ color: 'red' }}>{addMedicineError}</p>}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <button type="button" onClick={() => setShowAddMedicineModal(false)} disabled={addMedicineLoading}>Cancel</button>
                    <button type="submit" disabled={addMedicineLoading}>{addMedicineLoading ? 'Adding...' : 'Add Medicine'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Medicines Table */}
          {!medicineLoading && !medicineError && medicines.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
                <thead>
                  <tr style={{ background: '#eee' }}>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Medicine Name</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Generic Name</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Brand Name</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Type</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Dosage Form</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Strength</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Quantity</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Expiration</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Supplier Name</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Purchase Date</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Purchase Price</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Selling Price</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Batch Number</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Storage Location</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Reorder Level</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Reorder Quantity</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Status</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Remarks</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {medicines.map((medicine) => (
                    <tr key={medicine._id}>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.MEDICINE_NAME}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.GENERIC_NAME}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.BRAND_NAME}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.MEDICINE_TYPE}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.DOSAGE_FORM}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.STRENGTH}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.QUANTITY_IN_STOCK}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{new Date(medicine.EXPIRATION_DATE).toLocaleDateString()}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.SUPPLIER_NAME}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{new Date(medicine.PURCHASE_DATE).toLocaleDateString()}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.PURCHASE_PRICE}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.SELLING_PRICE}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.BATCH_NUMBER}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.STORAGE_LOCATION}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.REORDER_LEVEL}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.REORDER_QUANTITY}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.STATUS}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{medicine.REMARKS}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>
                        <button style={{ marginRight: '0.5rem' }} onClick={() => {
                          setEditMedicineData(medicine);
                          setShowEditMedicineModal(true);
                        }}>Edit</button>
                        <button onClick={() => handleDeleteMedicine(medicine._id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!medicineLoading && !medicineError && medicines.length === 0 && <p>No medicines found.</p>}

          {/* Edit Medicine Modal */}
          {showEditMedicineModal && editMedicineData && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', padding: '2rem', borderRadius: 8, minWidth: 350, maxHeight: '90vh', overflowY: 'auto' }}>
                <h3>Edit Medicine</h3>
                <form onSubmit={handleEditMedicineSubmit}>
                  {/* Medicine Fields */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Medicine Name:</label><br />
                    <input type="text" name="MEDICINE_NAME" value={editMedicineData.MEDICINE_NAME || ''} onChange={handleEditMedicineChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Generic Name:</label><br />
                    <input type="text" name="GENERIC_NAME" value={editMedicineData.GENERIC_NAME || ''} onChange={handleEditMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Brand Name:</label><br />
                    <input type="text" name="BRAND_NAME" value={editMedicineData.BRAND_NAME || ''} onChange={handleEditMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Medicine Type:</label><br />
                    <select name="MEDICINE_TYPE" value={editMedicineData.MEDICINE_TYPE || 'Tablet'} onChange={handleEditMedicineChange} required style={{ width: '100%', padding: '0.5rem' }}>
                      <option value="Tablet">Tablet</option>
                      <option value="Capsule">Capsule</option>
                      <option value="Syrup">Syrup</option>
                      <option value="Injection">Injection</option>
                      <option value="Ointment">Ointment</option>
                      <option value="Drops">Drops</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Dosage Form:</label><br />
                    <input type="text" name="DOSAGE_FORM" value={editMedicineData.DOSAGE_FORM || ''} onChange={handleEditMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Strength:</label><br />
                    <input type="text" name="STRENGTH" value={editMedicineData.STRENGTH || ''} onChange={handleEditMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Quantity In Stock:</label><br />
                    <input type="number" name="QUANTITY_IN_STOCK" value={editMedicineData.QUANTITY_IN_STOCK || 0} onChange={handleEditMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Expiration Date:</label><br />
                    <input type="date" name="EXPIRATION_DATE" value={editMedicineData.EXPIRATION_DATE ? editMedicineData.EXPIRATION_DATE.substring(0, 10) : ''} onChange={handleEditMedicineChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Supplier Name:</label><br />
                    <input type="text" name="SUPPLIER_NAME" value={editMedicineData.SUPPLIER_NAME || ''} onChange={handleEditMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Purchase Date:</label><br />
                    <input type="date" name="PURCHASE_DATE" value={editMedicineData.PURCHASE_DATE ? editMedicineData.PURCHASE_DATE.substring(0, 10) : ''} onChange={handleEditMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Purchase Price:</label><br />
                    <input type="number" name="PURCHASE_PRICE" value={editMedicineData.PURCHASE_PRICE || 0} onChange={handleEditMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Selling Price:</label><br />
                    <input type="number" name="SELLING_PRICE" value={editMedicineData.SELLING_PRICE || 0} onChange={handleEditMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Batch Number:</label><br />
                    <input type="text" name="BATCH_NUMBER" value={editMedicineData.BATCH_NUMBER || ''} onChange={handleEditMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Storage Location:</label><br />
                    <input type="text" name="STORAGE_LOCATION" value={editMedicineData.STORAGE_LOCATION || ''} onChange={handleEditMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Reorder Level:</label><br />
                    <input type="number" name="REORDER_LEVEL" value={editMedicineData.REORDER_LEVEL || 0} onChange={handleEditMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Reorder Quantity:</label><br />
                    <input type="number" name="REORDER_QUANTITY" value={editMedicineData.REORDER_QUANTITY || 1} onChange={handleEditMedicineChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Status:</label><br />
                    <select name="STATUS" value={editMedicineData.STATUS || 'Available'} onChange={handleEditMedicineChange} required style={{ width: '100%', padding: '0.5rem' }}>
                      <option value="Available">Available</option>
                      <option value="Out of Stock">Out of Stock</option>
                      <option value="Expired">Expired</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Remarks:</label><br />
                    <textarea name="REMARKS" value={editMedicineData.REMARKS || ''} onChange={handleEditMedicineChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>

                  {editMedicineError && <p style={{ color: 'red' }}>{editMedicineError}</p>}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <button type="button" onClick={() => setShowEditMedicineModal(false)} disabled={editMedicineLoading}>Cancel</button>
                    <button type="submit" disabled={editMedicineLoading}>{editMedicineLoading ? 'Saving...' : 'Save Changes'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedTab === 'procedureRooms' && (
        <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, color: '#495057' }}>🏥 Procedure Rooms Management</h3>
            <button 
              onClick={() => setShowAddProcedureRoomModal(true)} 
              style={{ 
                padding: '0.5rem 1rem', 
                backgroundColor: '#ffc107',
                color: '#212529',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Add Procedure Room
            </button>
          </div>

          {/* Add Procedure Room Modal */}
          {showAddProcedureRoomModal && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', padding: '2rem', borderRadius: 8, minWidth: 350, maxHeight: '90vh', overflowY: 'auto' }}>
                <h3>Add New Procedure Room</h3>
                <form onSubmit={handleAddProcedureRoomSubmit}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Room Name:</label><br />
                    <input type="text" name="ROOM_NAME" value={newProcedureRoomData.ROOM_NAME} onChange={handleAddProcedureRoomChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Location:</label><br />
                    <input type="text" name="LOCATION" value={newProcedureRoomData.LOCATION} onChange={handleAddProcedureRoomChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Capacity:</label><br />
                    <input type="number" name="CAPACITY" value={newProcedureRoomData.CAPACITY} onChange={handleAddProcedureRoomChange} required min="1" style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Equipment Available (comma-separated):</label><br />
                    <input type="text" name="EQUIPMENT_AVAILABLE" value={newProcedureRoomData.EQUIPMENT_AVAILABLE} onChange={handleAddProcedureRoomChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Status:</label><br />
                    <select name="STATUS" value={newProcedureRoomData.STATUS} onChange={handleAddProcedureRoomChange} style={{ width: '100%', padding: '0.5rem' }}>
                      <option value="Available">Available</option>
                      <option value="Occupied">Occupied</option>
                      <option value="Under Maintenance">Under Maintenance</option>
                    </select>
                  </div>

                  {addProcedureRoomError && <p style={{ color: 'red' }}>{addProcedureRoomError}</p>}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <button type="button" onClick={() => setShowAddProcedureRoomModal(false)} disabled={addProcedureRoomLoading}>Cancel</button>
                    <button type="submit" disabled={addProcedureRoomLoading}>{addProcedureRoomLoading ? 'Adding...' : 'Add Procedure Room'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Procedure Rooms Table */}
          {!procedureRoomLoading && !procedureRoomError && procedureRooms.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
                <thead>
                  <tr style={{ background: '#eee' }}>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Room Name</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Location</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Capacity</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Equipment Available</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Status</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {procedureRooms.map((room) => (
                    <tr key={room._id}>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{room.ROOM_NAME}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{room.LOCATION}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{room.CAPACITY}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{room.EQUIPMENT_AVAILABLE}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{room.STATUS}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>
                        <button style={{ marginRight: '0.5rem' }} onClick={() => {
                          setEditProcedureRoomData(room);
                          setShowEditProcedureRoomModal(true);
                        }}>Edit</button>
                        <button onClick={() => handleDeleteProcedureRoom(room._id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!procedureRoomLoading && !procedureRoomError && procedureRooms.length === 0 && <p>No procedure rooms found.</p>}

          {/* Edit Procedure Room Modal */}
          {showEditProcedureRoomModal && editProcedureRoomData && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', padding: '2rem', borderRadius: 8, minWidth: 350, maxHeight: '90vh', overflowY: 'auto' }}>
                <h3>Edit Procedure Room</h3>
                <form onSubmit={handleEditProcedureRoomSubmit}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Room Name:</label><br />
                    <input type="text" name="ROOM_NAME" value={editProcedureRoomData.ROOM_NAME || ''} onChange={handleEditProcedureRoomChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Location:</label><br />
                    <input type="text" name="LOCATION" value={editProcedureRoomData.LOCATION || ''} onChange={handleEditProcedureRoomChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Capacity:</label><br />
                    <input type="number" name="CAPACITY" value={editProcedureRoomData.CAPACITY || 1} onChange={handleEditProcedureRoomChange} required min="1" style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Equipment Available (comma-separated):</label><br />
                    <input type="text" name="EQUIPMENT_AVAILABLE" value={editProcedureRoomData.EQUIPMENT_AVAILABLE} onChange={handleEditProcedureRoomChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Status:</label><br />
                    <select name="STATUS" value={editProcedureRoomData.STATUS || 'Available'} onChange={handleEditProcedureRoomChange} style={{ width: '100%', padding: '0.5rem' }}>
                      <option value="Available">Available</option>
                      <option value="Occupied">Occupied</option>
                      <option value="Under Maintenance">Under Maintenance</option>
                    </select>
                  </div>

                  {editProcedureRoomError && <p style={{ color: 'red' }}>{editProcedureRoomError}</p>}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <button type="button" onClick={() => setShowEditProcedureRoomModal(false)} disabled={editProcedureRoomLoading}>Cancel</button>
                    <button type="submit" disabled={editProcedureRoomLoading}>{editProcedureRoomLoading ? 'Saving...' : 'Save Changes'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedTab === 'procedures' && (
        <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, color: '#495057' }}>⚕️ Procedures Management</h3>
            <button 
              onClick={() => setShowAddProcedureModal(true)} 
              style={{ 
                padding: '0.5rem 1rem', 
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Add Procedure
            </button>
          </div>

          {/* Add Procedure Modal */}
          {showAddProcedureModal && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', padding: '2rem', borderRadius: 8, minWidth: 350, maxHeight: '90vh', overflowY: 'auto' }}>
                <h3>Add New Procedure</h3>
                <form onSubmit={handleAddProcedureSubmit}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Procedure Name:</label><br />
                    <input type="text" name="PROCEDURE_NAME" value={newProcedureData.PROCEDURE_NAME} onChange={handleAddProcedureChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Procedure Description:</label><br />
                    <textarea name="PROCEDURE_DESCRIPTION" value={newProcedureData.PROCEDURE_DESCRIPTION} onChange={handleAddProcedureChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Procedure Type:</label><br />
                    <select name="PROCEDURE_TYPE" value={newProcedureData.PROCEDURE_TYPE} onChange={handleAddProcedureChange} required style={{ width: '100%', padding: '0.5rem' }}>
                      <option value="Surgical">Surgical</option>
                      <option value="Non-Surgical">Non-Surgical</option>
                      <option value="Therapeutic">Therapeutic</option>
                      <option value="Diagnostic">Diagnostic</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Department:</label><br />
                    <input type="text" name="DEPARTMENT" value={newProcedureData.DEPARTMENT} onChange={handleAddProcedureChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Duration (minutes):</label><br />
                    <input type="number" name="DURATION" value={newProcedureData.DURATION} onChange={handleAddProcedureChange} required min="1" style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Cost:</label><br />
                    <input type="number" name="COST" value={newProcedureData.COST} onChange={handleAddProcedureChange} required min="0" style={{ width: '100%' }} />
                  </div>
                  <ProcedureCourseFields data={newProcedureData} procedures={procedures} onChange={(patch) => setNewProcedureData((current) => ({ ...current, ...patch }))} />
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Insurance Coverage:</label><br />
                    <input type="checkbox" name="INSURANCE_COVERAGE" checked={newProcedureData.INSURANCE_COVERAGE} onChange={(e) => setNewProcedureData({ ...newProcedureData, INSURANCE_COVERAGE: e.target.checked })} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Preparation Required:</label><br />
                    <textarea name="PREPARATION_REQUIRED" value={newProcedureData.PREPARATION_REQUIRED} onChange={handleAddProcedureChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Recovery Time (days):</label><br />
                    <input type="number" name="RECOVERY_TIME" value={newProcedureData.RECOVERY_TIME} onChange={handleAddProcedureChange} min="0" style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Risks:</label><br />
                    <textarea name="RISKS" value={newProcedureData.RISKS} onChange={handleAddProcedureChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Benefits:</label><br />
                    <textarea name="BENEFITS" value={newProcedureData.BENEFITS} onChange={handleAddProcedureChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Consent Required:</label><br />
                    <input type="checkbox" name="CONSENT_REQUIRED" checked={newProcedureData.CONSENT_REQUIRED} onChange={(e) => setNewProcedureData({ ...newProcedureData, CONSENT_REQUIRED: e.target.checked })} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Equipment Used (comma-separated):</label><br />
                    <input type="text" name="EQUIPMENT_USED" value={newProcedureData.EQUIPMENT_USED.join(',')} onChange={(e) => setNewProcedureData({ ...newProcedureData, EQUIPMENT_USED: e.target.value.split(',').map(s => s.trim()) })} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Procedure Guidelines:</label><br />
                    <textarea name="PROCEDURE_GUIDELINES" value={newProcedureData.PROCEDURE_GUIDELINES} onChange={handleAddProcedureChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Aftercare Instructions:</label><br />
                    <textarea name="AFTERCARE_INSTRUCTIONS" value={newProcedureData.AFTERCARE_INSTRUCTIONS} onChange={handleAddProcedureChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Frequency:</label><br />
                    <input type="text" name="FREQUENCY" value={newProcedureData.FREQUENCY} onChange={handleAddProcedureChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Available Dates (comma-separated YYYY-MM-DD):</label><br />
                    <input type="text" name="AVAILABLE_DATES" value={newProcedureData.AVAILABLE_DATES.map(date => { const d = new Date(date); return isNaN(d.getTime()) ? '' : d.toISOString().substring(0, 10); }).join(',')} onChange={(e) => {
                      const rawDates = e.target.value.split(',').map(s => s.trim());
                      const formattedDates = rawDates.filter(dateStr => {
                        const d = new Date(dateStr);
                        return !isNaN(d.getTime());
                      }).map(dateStr => new Date(dateStr).toISOString().substring(0, 10));
                      setNewProcedureData(prevData => ({ ...prevData, AVAILABLE_DATES: formattedDates }));
                    }} style={{ width: '100%' }} />
                  </div>

                  {addProcedureError && <p style={{ color: 'red' }}>{addProcedureError}</p>}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <button type="button" onClick={() => setShowAddProcedureModal(false)} disabled={addProcedureLoading}>Cancel</button>
                    <button type="submit" disabled={addProcedureLoading}>{addProcedureLoading ? 'Adding...' : 'Add Procedure'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Procedures Table */}
          {!procedureLoading && !procedureError && procedures.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
                <thead>
                  <tr style={{ background: '#eee' }}>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Procedure Name</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Description</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Type</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Course</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Department</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Duration (min)</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Cost</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Insurance</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Preparation</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Recovery (days)</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Risks</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Benefits</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Consent</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Equipment</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Guidelines</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Aftercare</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Frequency</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Available Dates</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {procedures.map((procedure) => (
                    <tr key={procedure._id}>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.PROCEDURE_NAME}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.PROCEDURE_DESCRIPTION}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.PROCEDURE_TYPE}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{courseSummary(procedure)}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.DEPARTMENT}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.DURATION}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.COST}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.INSURANCE_COVERAGE ? 'Yes' : 'No'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.PREPARATION_REQUIRED}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.RECOVERY_TIME}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.RISKS}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.BENEFITS}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.CONSENT_REQUIRED ? 'Yes' : 'No'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.EQUIPMENT_USED ? procedure.EQUIPMENT_USED.join(', ') : 'N/A'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.PROCEDURE_GUIDELINES}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.AFTERCARE_INSTRUCTIONS}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.FREQUENCY}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{procedure.AVAILABLE_DATES ? procedure.AVAILABLE_DATES.map(date => new Date(date).toLocaleDateString()).join(', ') : 'N/A'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>
                        <button style={{ marginRight: '0.5rem' }} onClick={() => {
                          setEditProcedureData(procedure);
                          setShowEditProcedureModal(true);
                        }}>Edit</button>
                        <button onClick={() => handleDeleteProcedure(procedure._id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!procedureLoading && !procedureError && procedures.length === 0 && <p>No procedures found.</p>}

          {/* Edit Procedure Modal */}
          {showEditProcedureModal && editProcedureData && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', padding: '2rem', borderRadius: 8, minWidth: 350, maxHeight: '90vh', overflowY: 'auto' }}>
                <h3>Edit Procedure</h3>
                <form onSubmit={handleEditProcedureSubmit}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Procedure Name:</label><br />
                    <input type="text" name="PROCEDURE_NAME" value={editProcedureData.PROCEDURE_NAME || ''} onChange={handleEditProcedureChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Procedure Description:</label><br />
                    <textarea name="PROCEDURE_DESCRIPTION" value={editProcedureData.PROCEDURE_DESCRIPTION || ''} onChange={handleEditProcedureChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Procedure Type:</label><br />
                    <select name="PROCEDURE_TYPE" value={editProcedureData.PROCEDURE_TYPE || 'Surgical'} onChange={handleEditProcedureChange} required style={{ width: '100%', padding: '0.5rem' }}>
                      <option value="Surgical">Surgical</option>
                      <option value="Non-Surgical">Non-Surgical</option>
                      <option value="Therapeutic">Therapeutic</option>
                      <option value="Diagnostic">Diagnostic</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Department:</label><br />
                    <input type="text" name="DEPARTMENT" value={editProcedureData.DEPARTMENT || ''} onChange={handleEditProcedureChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Duration (minutes):</label><br />
                    <input type="number" name="DURATION" value={editProcedureData.DURATION || 1} onChange={handleEditProcedureChange} required min="1" style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Cost:</label><br />
                    <input type="number" name="COST" value={editProcedureData.COST || 0} onChange={handleEditProcedureChange} required min="0" style={{ width: '100%' }} />
                  </div>
                  <ProcedureCourseFields data={editProcedureData} procedures={procedures} selfId={editProcedureData._id} onChange={(patch) => setEditProcedureData((current) => ({ ...current, ...patch }))} />
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Insurance Coverage:</label><br />
                    <input type="checkbox" name="INSURANCE_COVERAGE" checked={editProcedureData.INSURANCE_COVERAGE} onChange={(e) => setEditProcedureData({ ...editProcedureData, INSURANCE_COVERAGE: e.target.checked })} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Preparation Required:</label><br />
                    <textarea name="PREPARATION_REQUIRED" value={editProcedureData.PREPARATION_REQUIRED || ''} onChange={handleEditProcedureChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Recovery Time (days):</label><br />
                    <input type="number" name="RECOVERY_TIME" value={editProcedureData.RECOVERY_TIME || 0} onChange={handleEditProcedureChange} min="0" style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Risks:</label><br />
                    <textarea name="RISKS" value={editProcedureData.RISKS || ''} onChange={handleEditProcedureChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Benefits:</label><br />
                    <textarea name="BENEFITS" value={editProcedureData.BENEFITS || ''} onChange={handleEditProcedureChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Consent Required:</label><br />
                    <input type="checkbox" name="CONSENT_REQUIRED" checked={editProcedureData.CONSENT_REQUIRED} onChange={(e) => setEditProcedureData({ ...editProcedureData, CONSENT_REQUIRED: e.target.checked })} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Equipment Used (comma-separated):</label><br />
                    <input type="text" name="EQUIPMENT_USED" value={editProcedureData.EQUIPMENT_USED ? editProcedureData.EQUIPMENT_USED.join(',') : ''} onChange={(e) => setEditProcedureData({ ...editProcedureData, EQUIPMENT_USED: e.target.value.split(',').map(s => s.trim()) })} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Procedure Guidelines:</label><br />
                    <textarea name="PROCEDURE_GUIDELINES" value={editProcedureData.PROCEDURE_GUIDELINES || ''} onChange={handleEditProcedureChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Aftercare Instructions:</label><br />
                    <textarea name="AFTERCARE_INSTRUCTIONS" value={editProcedureData.AFTERCARE_INSTRUCTIONS || ''} onChange={handleEditProcedureChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Frequency:</label><br />
                    <input type="text" name="FREQUENCY" value={editProcedureData.FREQUENCY || ''} onChange={handleEditProcedureChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Available Dates (comma-separated YYYY-MM-DD):</label><br />
                    <input type="text" name="AVAILABLE_DATES" value={editProcedureData.AVAILABLE_DATES ? editProcedureData.AVAILABLE_DATES.map(date => new Date(date).toISOString().substring(0, 10)).join(',') : ''} onChange={(e) => setEditProcedureData({ ...editProcedureData, AVAILABLE_DATES: e.target.value.split(',').map(s => s.trim()) })} style={{ width: '100%' }} />
                  </div>

                  {editProcedureError && <p style={{ color: 'red' }}>{editProcedureError}</p>}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <button type="button" onClick={() => setShowEditProcedureModal(false)} disabled={editProcedureLoading}>Cancel</button>
                    <button type="submit" disabled={editProcedureLoading}>{editProcedureLoading ? 'Saving...' : 'Save Changes'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedTab === 'therapists' && (
        <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, color: '#495057' }}>🏥 Therapists Management</h3>
            <button 
              onClick={() => setShowAddTherapistModal(true)} 
              style={{ 
                padding: '0.5rem 1rem', 
                backgroundColor: '#6f42c1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Add Therapist
            </button>
          </div>

          {/* Add Therapist Modal */}
          {showAddTherapistModal && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', padding: '2rem', borderRadius: 8, minWidth: 350, maxHeight: '90vh', overflowY: 'auto' }}>
                <h3>Add New Therapist</h3>
                <form onSubmit={handleAddTherapistSubmit}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Therapist Name:</label><br />
                    <input type="text" name="THERAPIST_NAME" value={newTherapistData.THERAPIST_NAME} onChange={handleAddTherapistChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Specialization:</label><br />
                    <input type="text" name="SPECIALIZATION" value={newTherapistData.SPECIALIZATION} onChange={handleAddTherapistChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Qualifications:</label><br />
                    <input type="text" name="QUALIFICATIONS" value={newTherapistData.QUALIFICATIONS} onChange={handleAddTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Experience (Years):</label><br />
                    <input type="number" name="EXPERIENCE_YEARS" value={newTherapistData.EXPERIENCE_YEARS} onChange={handleAddTherapistChange} required min="0" style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Contact Number:</label><br />
                    <input type="tel" name="CONTACT_NUMBER" value={newTherapistData.CONTACT_NUMBER} onChange={handleAddTherapistChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Email:</label><br />
                    <input type="email" name="EMAIL" value={newTherapistData.EMAIL} onChange={handleAddTherapistChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Consultation Hours:</label><br />
                    <input type="text" name="CONSULTATION_HOURS" value={newTherapistData.CONSULTATION_HOURS} onChange={handleAddTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Clinic Location:</label><br />
                    <input type="text" name="CLINIC_LOCATION" value={newTherapistData.CLINIC_LOCATION} onChange={handleAddTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Fees:</label><br />
                    <input type="number" name="FEES" value={newTherapistData.FEES} onChange={handleAddTherapistChange} required min="0" style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Status:</label><br />
                    <select name="STATUS" value={newTherapistData.STATUS} onChange={handleAddTherapistChange} style={{ width: '100%', padding: '0.5rem' }}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="On Leave">On Leave</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Photo ID:</label><br />
                    <input type="text" name="PHOTO_ID" value={newTherapistData.PHOTO_ID} onChange={handleAddTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Department:</label><br />
                    <input type="text" name="DEPARTMENT" value={newTherapistData.DEPARTMENT} onChange={handleAddTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Biography:</label><br />
                    <textarea name="BIOGRAPHY" value={newTherapistData.BIOGRAPHY} onChange={handleAddTherapistChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Languages Spoken (comma-separated):</label><br />
                    <input type="text" name="LANGUAGES_SPOKEN" value={newTherapistData.LANGUAGES_SPOKEN} onChange={handleAddTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Available Days (comma-separated: Monday, Tuesday, etc.):</label><br />
                    <input type="text" name="AVAILABLE_DAYS" value={newTherapistData.AVAILABLE_DAYS} onChange={handleAddTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Procedures Performed (comma-separated):</label><br />
                    <input type="text" name="PROCEDURES_PERFORMED" value={newTherapistData.PROCEDURES_PERFORMED} onChange={handleAddTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Certifications (comma-separated):</label><br />
                    <input type="text" name="CERTIFICATIONS" value={newTherapistData.CERTIFICATIONS} onChange={handleAddTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Feedback Score (1-5):</label><br />
                    <input type="number" name="FEEDBACK_SCORE" value={newTherapistData.FEEDBACK_SCORE || ''} onChange={handleAddTherapistChange} min="1" max="5" style={{ width: '100%' }} />
                  </div>

                  {addTherapistError && <p style={{ color: 'red' }}>{addTherapistError}</p>}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <button type="button" onClick={() => setShowAddTherapistModal(false)} disabled={addTherapistLoading}>Cancel</button>
                    <button type="submit" disabled={addTherapistLoading}>{addTherapistLoading ? 'Adding...' : 'Add Therapist'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Therapists Table */}
          {!therapistLoading && !therapistError && therapists.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
                <thead>
                  <tr style={{ background: '#eee' }}>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Therapist Name</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Specialization</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Qualifications</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Experience (Years)</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Contact Number</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Email</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Consultation Hours</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Clinic Location</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Fees</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Status</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Department</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Languages Spoken</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Available Days</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Procedures Performed</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Certifications</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Feedback Score</th>
                    <th style={{ border: '1px solid #ccc', padding: '0.5rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {therapists.map((therapist) => (
                    <tr key={therapist._id}>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.THERAPIST_NAME}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.SPECIALIZATION}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.QUALIFICATIONS}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.EXPERIENCE_YEARS}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.CONTACT_NUMBER}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.EMAIL}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.CONSULTATION_HOURS}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.CLINIC_LOCATION}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.FEES}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.STATUS}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.DEPARTMENT}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.LANGUAGES_SPOKEN ? therapist.LANGUAGES_SPOKEN.join(', ') : 'N/A'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.AVAILABLE_DAYS ? therapist.AVAILABLE_DAYS.join(', ') : 'N/A'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.PROCEDURES_PERFORMED ? therapist.PROCEDURES_PERFORMED.join(', ') : 'N/A'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.CERTIFICATIONS ? therapist.CERTIFICATIONS.join(', ') : 'N/A'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>{therapist.FEEDBACK_SCORE}</td>
                      <td style={{ border: '1px solid #ccc', padding: '0.5rem' }}>
                        <button style={{ marginRight: '0.5rem' }} onClick={() => {
                          setEditTherapistData(therapist);
                          setShowEditTherapistModal(true);
                        }}>Edit</button>
                        <button onClick={() => handleDeleteTherapist(therapist._id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!therapistLoading && !therapistError && therapists.length === 0 && <p>No therapists found.</p>}

          {/* Edit Therapist Modal */}
          {showEditTherapistModal && editTherapistData && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', padding: '2rem', borderRadius: 8, minWidth: 350, maxHeight: '90vh', overflowY: 'auto' }}>
                <h3>Edit Therapist</h3>
                <form onSubmit={handleEditTherapistSubmit}>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Therapist Name:</label><br />
                    <input type="text" name="THERAPIST_NAME" value={editTherapistData.THERAPIST_NAME || ''} onChange={handleEditTherapistChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Specialization:</label><br />
                    <input type="text" name="SPECIALIZATION" value={editTherapistData.SPECIALIZATION || ''} onChange={handleEditTherapistChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Qualifications:</label><br />
                    <input type="text" name="QUALIFICATIONS" value={editTherapistData.QUALIFICATIONS || ''} onChange={handleEditTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Experience (Years):</label><br />
                    <input type="number" name="EXPERIENCE_YEARS" value={editTherapistData.EXPERIENCE_YEARS || 0} onChange={handleEditTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Contact Number:</label><br />
                    <input type="tel" name="CONTACT_NUMBER" value={editTherapistData.CONTACT_NUMBER || ''} onChange={handleEditTherapistChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Email:</label><br />
                    <input type="email" name="EMAIL" value={editTherapistData.EMAIL || ''} onChange={handleEditTherapistChange} required style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Consultation Hours:</label><br />
                    <input type="text" name="CONSULTATION_HOURS" value={editTherapistData.CONSULTATION_HOURS || ''} onChange={handleEditTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Clinic Location:</label><br />
                    <input type="text" name="CLINIC_LOCATION" value={editTherapistData.CLINIC_LOCATION || ''} onChange={handleEditTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Fees:</label><br />
                    <input type="number" name="FEES" value={editTherapistData.FEES || 0} onChange={handleEditTherapistChange} required min="0" style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Status:</label><br />
                    <select name="STATUS" value={editTherapistData.STATUS || 'Active'} onChange={handleEditTherapistChange} style={{ width: '100%', padding: '0.5rem' }}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="On Leave">On Leave</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Photo ID:</label><br />
                    <input type="text" name="PHOTO_ID" value={editTherapistData.PHOTO_ID || ''} onChange={handleEditTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Department:</label><br />
                    <input type="text" name="DEPARTMENT" value={editTherapistData.DEPARTMENT || ''} onChange={handleEditTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Biography:</label><br />
                    <textarea name="BIOGRAPHY" value={editTherapistData.BIOGRAPHY || ''} onChange={handleEditTherapistChange} style={{ width: '100%', minHeight: '80px' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Languages Spoken (comma-separated):</label><br />
                    <input type="text" name="LANGUAGES_SPOKEN" value={editTherapistData.LANGUAGES_SPOKEN || ''} onChange={handleEditTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Available Days (comma-separated: Monday, Tuesday, etc.):</label><br />
                    <input type="text" name="AVAILABLE_DAYS" value={editTherapistData.AVAILABLE_DAYS || ''} onChange={handleEditTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Procedures Performed (comma-separated):</label><br />
                    <input type="text" name="PROCEDURES_PERFORMED" value={editTherapistData.PROCEDURES_PERFORMED || ''} onChange={handleEditTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Certifications (comma-separated):</label><br />
                    <input type="text" name="CERTIFICATIONS" value={editTherapistData.CERTIFICATIONS || ''} onChange={handleEditTherapistChange} style={{ width: '100%' }} />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label>Feedback Score (1-5):</label><br />
                    <input type="number" name="FEEDBACK_SCORE" value={editTherapistData.FEEDBACK_SCORE || ''} onChange={handleEditTherapistChange} min="1" max="5" style={{ width: '100%' }} />
                  </div>

                  {editTherapistError && <p style={{ color: 'red' }}>{editTherapistError}</p>}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <button type="button" onClick={() => setShowEditTherapistModal(false)} disabled={editTherapistLoading}>Cancel</button>
                    <button type="submit" disabled={editTherapistLoading}>{editTherapistLoading ? 'Saving...' : 'Save Changes'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedTab === 'labTests' && (
        <div style={{ padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, color: '#495057' }}>🧪 Lab Tests Management</h3>
            <button 
              onClick={() => setShowAddLabTestModal(true)}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Add Lab Test
            </button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Test Code</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Test Name</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Category</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Sample Type</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Duration (hrs)</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Cost (₹)</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {labTests.map((test) => (
                  <tr key={test.LAB_TEST_ID}>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{test.TEST_CODE}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{test.TEST_NAME}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{test.TEST_CATEGORY}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{test.SAMPLE_TYPE}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{test.TEST_DURATION}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>₹{test.COST}</td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        backgroundColor: test.STATUS === 'Active' ? '#d4edda' : '#f8d7da',
                        color: test.STATUS === 'Active' ? '#155724' : '#721c24'
                      }}>
                        {test.STATUS}
                      </span>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <button 
                        onClick={() => {
                          setEditLabTestData(test);
                          setShowEditLabTestModal(true);
                        }}
                        style={{
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          marginRight: '0.5rem'
                        }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {labTests.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
              No lab tests found. Add your first lab test to get started.
            </div>
          )}
        </div>
      )}

      {/* Add Lab Test Modal */}
      {showAddLabTestModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#dc3545' }}>Add New Lab Test</h3>
            
            <form onSubmit={handleAddLabTestSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Test Name *</label>
                <input
                  type="text"
                  name="TEST_NAME"
                  value={newLabTestData.TEST_NAME}
                  onChange={handleAddLabTestChange}
                  required
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Test Code *</label>
                <input
                  type="text"
                  name="TEST_CODE"
                  value={newLabTestData.TEST_CODE}
                  onChange={handleAddLabTestChange}
                  required
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  placeholder="e.g., CBC, FBS, LFT"
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Test Category *</label>
                <select
                  name="TEST_CATEGORY"
                  value={newLabTestData.TEST_CATEGORY}
                  onChange={handleAddLabTestChange}
                  required
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="Blood Test">Blood Test</option>
                  <option value="Urine Test">Urine Test</option>
                  <option value="Stool Test">Stool Test</option>
                  <option value="Imaging">Imaging</option>
                  <option value="Cardiology">Cardiology</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Cost (₹) *</label>
                <input
                  type="number"
                  name="COST"
                  value={newLabTestData.COST}
                  onChange={handleAddLabTestChange}
                  min="0"
                  step="0.01"
                  required
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Sample Type *</label>
                <select
                  name="SAMPLE_TYPE"
                  value={newLabTestData.SAMPLE_TYPE}
                  onChange={handleAddLabTestChange}
                  required
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="Blood">Blood</option>
                  <option value="Urine">Urine</option>
                  <option value="Stool">Stool</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {addLabTestError && (
                <div style={{ color: 'red', marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px' }}>
                  {addLabTestError}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <button
                  type="button"
                  onClick={() => setShowAddLabTestModal(false)}
                  disabled={addLabTestLoading}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addLabTestLoading}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    opacity: addLabTestLoading ? 0.6 : 1
                  }}
                >
                  {addLabTestLoading ? 'Adding...' : 'Add Lab Test'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditLabTestModal && editLabTestData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#dc3545' }}>Edit Lab Test</h3>
            <form onSubmit={handleEditLabTestSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Test Name *</label>
                <input type="text" name="TEST_NAME" value={editLabTestData.TEST_NAME || ''} onChange={handleEditLabTestChange} required style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Test Code *</label>
                <input type="text" name="TEST_CODE" value={editLabTestData.TEST_CODE || ''} onChange={handleEditLabTestChange} required style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Test Category *</label>
                <select name="TEST_CATEGORY" value={editLabTestData.TEST_CATEGORY || 'Blood Test'} onChange={handleEditLabTestChange} required style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                  <option value="Blood Test">Blood Test</option>
                  <option value="Urine Test">Urine Test</option>
                  <option value="Stool Test">Stool Test</option>
                  <option value="Imaging">Imaging</option>
                  <option value="Cardiology">Cardiology</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Cost (₹) *</label>
                <input type="number" name="COST" value={editLabTestData.COST ?? 0} onChange={handleEditLabTestChange} min="0" step="0.01" required style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }} />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Sample Type *</label>
                <select name="SAMPLE_TYPE" value={editLabTestData.SAMPLE_TYPE || 'Blood'} onChange={handleEditLabTestChange} required style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                  <option value="Blood">Blood</option>
                  <option value="Urine">Urine</option>
                  <option value="Stool">Stool</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Status</label>
                <select name="STATUS" value={editLabTestData.STATUS || 'Active'} onChange={handleEditLabTestChange} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Discontinued">Discontinued</option>
                </select>
              </div>
              {editLabTestError && (
                <div style={{ color: 'red', marginBottom: '1rem', padding: '0.5rem', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px' }}>
                  {editLabTestError}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                <button type="button" onClick={() => setShowEditLabTestModal(false)} disabled={editLabTestLoading} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={editLabTestLoading} style={{ padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                  {editLabTestLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferenceDataManagement;
